interface ITimeoutSchedule {
  promise: Promise<any>;
}

export enum ActivityTypeEnum {
  note = 1,
  attachment = 2,
  inboundEmail = 3,
  outboundEmail = 4,
  remoteControl = 5,
  deviceLog = 6,
  deviceSnapshot = 7,
  deviceVideo = 8,
  deviceScreenshot = 9,
}

export interface UserInfo {
  userId: string;
  userName: string;
}

export interface Activity {
  activityId: number;
  activityTypeId: number;
  createdBy: string;
  creationDate: string;
  description: string;
  details: any; // This depends on activity type
  updateDate: string;
  updatedBy: string;

  // from IActivityModel:
  creationUserId: string;
  creationUserPublicId?: string;
  updateUserId?: number;
  updateUserPublicId?: string;
  parentActivityId?: number;
  childActivities: Activity[];
  isDirty: () => boolean;
  getCustomIconClass?: () => string;
  $repeaterId?: string;
  $$creationUserInfo?: UserInfo;
  $$updateUserInfo?: UserInfo;
}

export interface TicketActivity extends Activity {
  /** The ticket id */
  ticketId: string;
}

export interface IDeviceModel extends IAssociatedDeviceModel {
  /** The name of the device */
  deviceName: string;
  /** The Url to mobicontrol device page */
  deviceDetailsUrl: string;
  /** The kind of device */
  kind: string;
  /** The os version */
  osVersion: string;
  /** The name or IP address of the device */
  hostName: string;
  /** MAC address */
  macAddress: string;
  /** Device manufacturer */
  manufacturer: string;
  /** Device model */
  model: string;
  /** Phone number */
  phoneNumber: string;
  /** Hardware serial number */
  hardwareSerialNumber: string;
  /** User name */
  userName: string;
  /** User phone number */
  userPhoneNumber: string;
  /** IMEI */
  imei: string;
  /** The MobiControl device ID */
  mcDeviceId: string;
  /** The MC version */
  mcVersion: string;
}

export interface IAssociatedDeviceModel {
  id: number;
  /** The device identifier */
  deviceId: string;
  /** True if the device agent is online; otherwise, false */
  isAgentOnline: boolean;
  /** The family of the device */
  family: string;
  /** True if the device agent supports remote view only; otherwise, false */
  isRemoteViewOnly: boolean;
  /** The external MC connection identifier */
  mcConnectionId?: number;
  /** The external MC connection name */
  mcConnectionName: string;
  /** True if the device agent supports remote control; otherwise, false */
  supportsRC: boolean;
}

export type INewAssociatedDeviceModel = INewAssociatedManagedDeviceModel & INewAssociatedUnmanagedDeviceModel;

export interface INewAssociatedManagedDeviceModel {
  ticketId: string;
  deviceId: string;
  deviceAssociationId: string;
}

export interface INewAssociatedUnmanagedDeviceModel {
  deviceName: string;
}

export interface AttachmentActivity extends Activity {
  /** The file Id */
  fileId: string;
  /** The original file name of the attachment */
  fileName: string;
  /** The file size, in bytes */
  fileSize: number;
  /** The friendly name for the attachment */
  displayName?: string;
  /** Base64 png encoded string for thumbnail */
  thumbnail?: string;
  /** The mcInstance Name that shows where is this attachment from */
  $mcInstanceName: string;
  /** Client-side file object for upload ($$ strips property from JSON serialization) */
  $$file: File;
  /** Whether the attachment is local only */
  $$isLocal: boolean;
  /** The preview id for streaming */
  $$previewId: string;
  /**
   * Callback used to notify about attachment upload completion/failure.
   *
   * @param error Error object if there was an upload failure, otherwise undefined.
   */
  onUploadComplete: (result: UploadCompleteReason) => void;
}

export const enum UploadCompleteReason {
  /** The upload was successful */
  Success,
  /** The upload was cancelled by the user */
  Cancelled,
  /** There was an error during the upload */
  Error,
}

export interface TagModel {
  /** The unique tag ID */
  tagId?: number;
  /** The associated tag title */
  title: string;
  /** The associated tagGroup Id */
  tagGroupId?: number;
}
export class Ticket {
  public activities: Activity[];
  public devices: IDeviceModel[];
  /** The ticket ID */
  public ticketId: string; // Note: These are actually numbers for netCore
  /** The reporter ID */
  public reporterPublicId: string;
  /** The assignee ID */
  public assigneePublicId?: string;
  /** The creation user ID */
  public creationUserPublicId?: string;
  /** The resolution user ID */
  public resolutionUserPublicId?: string;
  /** The reporter Name */
  public reporterName: string;
  /** The title of the ticket */
  public title: string;
  /** Tags list */
  public associatedTags: TagModel[] = [];
  /** Should hide extra tags */
  public hideTags = true;
  /** Should update tags list */
  public update: { toggle: boolean } = { toggle: false };
  /** The status of the ticket */
  public status: string;
  /** The status of the ticket */
  public statusCaption: string;
  /** The status icon of the ticket */
  public statusGlyphIcon: string;
  /** The priority text of the ticket */
  public priorityCaption: string;
  /** The priority icon of the ticket */
  public priorityGlyphIcon: string;
  /** The assignee of the ticket */
  public assignee: string;

  public reportedMethod: string;
  /** The method by which the ticket was reported */
  public reportedMethodGlyphIcon: string;
  /** The creation date/time of the ticket */
  public creationDate: string;
  /** The creation user of the ticket */
  public creationUser: string;
  /** The last updated date/time of the ticket */
  public updateDate: string;
  /** The group name */
  public groupName: string;
  public groupId: number;
  /** List of filtered fields */
  public filteredFields: string;
  /** Due date for the ticket */
  public dueDate: string;
  /** Short group name for the ticket */
  public shortGroupName: string;

  public description: string;

  private timeout?: ITimeoutSchedule;

  public allowableActions: string[];
  /** The priority of the ticket */
  public priority: string;
  /** The activity ID */
  public activityId: number;

  public isDeleted: boolean;
}

export interface NoteActivity extends Activity {
  details: {
    isPrivate: boolean;
    isResolution: boolean;
  };
}
